import React, { useCallback, useRef } from 'react';
import { Button } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { getJWtDetails, showToastMessage } from '../../utils/AppUtils';
import { useDispatch } from 'react-redux';
import { followAThspian, resetFollow, unfollowThspian } from '../../redux/audience/action';
import { UserSummary } from '../../types/global/helper';
import { TOrigin } from '../../redux/audience/model';
import { useSelector } from 'react-redux';
import { followingSelector } from '../../pages/Dashboard/selectors';
import { useAuth } from '../../module/AuthenticationModule';
import { toggleAuthModal } from '../../redux/authentication/action';
import { throttle } from 'lodash';

export interface IFollowButtonProps {
    isFollowing?: boolean;
    onClickFollow?: (user?: UserSummary, isSinglePreview?: boolean) => void; // Single Preview allows us to also modify the follow value when previewing a follw button from a user page
    user?: UserSummary;
    intrinsic?: boolean;
    location?: TOrigin;
}
const EsFollowButton: React.FC<IFollowButtonProps> = ({ isFollowing, onClickFollow, user, intrinsic, location }) => {
    const { themeStyle } = useThemeContext();
    const { user_id } = getJWtDetails();
    const dispatch = useDispatch();
    const isFollowingInProgress = useSelector(followingSelector);
    const rapidTapShown = useRef(false);
    const { isSignedIn } = useAuth();

    const throttledWarning = throttle(() => {
        showToastMessage('Rapid Tap Detected. Slow down', 'warning');
        dispatch(resetFollow());
    }, 3000); // show once every 2 seconds

    const handleInternalFollow = useCallback(() => {
        if (!isSignedIn) {
            dispatch(toggleAuthModal(true));
            return;
        }
        if (intrinsic && user?._id) {
            if (!isFollowingInProgress) {
                if (!isFollowing) {
                    dispatch(
                        followAThspian({
                            following: user?._id,
                            from: location,
                        }),
                    );
                } else if (
                    user &&
                    user.user_follow_interaction &&
                    user.user_follow_interaction[0]._id !== 'temporary'
                ) {
                    dispatch(
                        unfollowThspian({
                            documentId: user.user_follow_interaction[0]._id,
                            additionalPayload: user.user_follow_interaction[0],
                        }),
                    );
                }
            } else {
                if (!rapidTapShown.current) {
                    throttledWarning();
                    rapidTapShown.current = true;
                }
            }
        } else {
            onClickFollow && onClickFollow(user as unknown as UserSummary);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intrinsic, onClickFollow, user, isFollowingInProgress]);

    if (intrinsic) {
        if (user_id === user?._id) {
            return null;
        }
    }

    return (
        <Button
            style={{
                height: '40px',
                padding: '10px',
                minWidth: 'fit-content',
                borderRadius: 100,
                fontSize: 14,
                fontWeight: '600',
                fontFamily: FONT_ENUM.MAIN_BOLD,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
            }}
            onClick={handleInternalFollow}
            sx={{
                textDecoration: 'none',
                backgroundColor: isFollowing ? 'transparent' : theme[themeStyle].primary_orange,
                color: isFollowing ? theme[themeStyle].primary_orange : theme[themeStyle].pure_black,
                textTransform: 'none',
                transition: 'all 0.3s ease-in-out',
                border: `1px solid ${theme[themeStyle].primary_orange}`,
                width: '110px',
                '&:hover': {
                    border: isFollowing
                        ? `1px solid ${theme[themeStyle].pure_black}`
                        : `1px solid ${theme[themeStyle].primary_orange}`,
                    backgroundColor: isFollowing ? theme[themeStyle].primary_orange : 'transparent',
                    color: isFollowing ? theme[themeStyle].black : theme[themeStyle].primary_orange,
                },
            }}>
            {isFollowing ? 'Following' : 'Follow'}
        </Button>
    );
};

export { EsFollowButton };
